import { type MouseEvent, useState } from 'react';

import type { TaxedMoneyFragment } from '@/graphql/saleor/api';
import { useStrapPurchaseFlow } from '@/hooks/useStrapPurchaseFlow';
import { getMostRelevantProductCta } from '@/integrations/orderApp';
import { getBoolValue } from '@/lib/attributes';
import { getPricingFormProductChannelListing } from '@/lib/product/helpers';
import type { CtaType, Maybe } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { AddToBagDrawer } from '@/views/StrapView/components/AddToBagDrawer';

import {
  WishProductCard,
  type WishProductCardProps,
} from '../WishProductCard/WishProductCard';

export const StrapProductCard = ({
  addToCart,
  handleChange,
  handleChecked,
  isCheckoutProcessing,
  isClosable,
  isProcessing,
  product,
  productsLength,
  removeFavoriteItem,
}: WishProductCardProps) => {
  const {
    locale,
    query: { channel },
  } = useRegionProvider();
  const productId = product.id;
  const sku = product.slug;
  const skus = [product.slug].concat(
    (product?.otherSizesSkus?.values?.map((value) => value.name) ?? []).filter(
      (name): name is string => name !== null && name !== undefined,
    ),
  );

  const {
    buckles,
    handlePurchase,
    isDrawerOpen: isAddToBagModalOpen,
    setIsDrawerOpen: setIsAddToBagModalOpen,
    sizeState,
    strapData,
    strapSizeDatas,
    tools,
  } = useStrapPurchaseFlow(sku, productId, skus);

  const [isLocalProcessing, setIsLocalProcessing] = useState(false);

  const cta = getMostRelevantProductCta(strapSizeDatas, product.slug);

  const pricing = getPricingFormProductChannelListing(product, channel);

  const handleAddToCart = async (
    e: MouseEvent<HTMLElement>,
    id: Maybe<string>,
    sku: Maybe<string>,
    cta: CtaType | undefined,
  ) => {
    setIsLocalProcessing(true);
    const shouldOpenDrawer = await handlePurchase(sku ?? '', skus);

    if (shouldOpenDrawer !== false) {
      setIsLocalProcessing(false);
      return;
    }

    addToCart(e, id, sku, cta);
    setIsLocalProcessing(false);
  };

  return (
    <>
      <WishProductCard
        key={product.id}
        addToCart={handleAddToCart}
        data-testid='productCard'
        handleChange={handleChange}
        handleChecked={handleChecked}
        isCheckoutProcessing={isCheckoutProcessing}
        isClosable={isClosable}
        isProcessing={isProcessing || isLocalProcessing}
        product={product}
        productCta={cta}
        productsLength={productsLength}
        removeFavoriteItem={removeFavoriteItem}
      />
      {strapData.businessData && (
        <AddToBagDrawer
          buckles={buckles}
          businessData={strapData.businessData}
          cta={cta}
          data-test-id='StrapCardAddToBagDrawer'
          isOpen={isAddToBagModalOpen}
          niceToHaveProducts={tools}
          setIsOpen={setIsAddToBagModalOpen}
          sizeState={sizeState}
          strap={{
            currency: pricing?.gross?.currency,
            integrated_buckle: getBoolValue(product.integratedBuckle),
            name: { [locale.code.toUpperCase()]: product.name },
            price: pricing?.gross?.amount,
            thumbnail_url: product.thumbnail?.url,
          }}
          strapId={
            sizeState.sizesSelectedItem
              ? sizeState.sizesSelectedItem.value
              : (productId ?? '')
          }
          strapPrice={
            strapData.variantDetailsData?.productVariant?.pricing
              ?.price as TaxedMoneyFragment
          }
          strapSku={
            sizeState.sizesSelectedItem
              ? (
                  sizeState.sizesSelectedItem.data as {
                    id: string;
                    sku: string;
                  }
                )?.sku
              : sku
          }
        />
      )}
    </>
  );
};
StrapProductCard.displayName = 'StrapProductCard';
